import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ArrowLeft, ChevronDown, ChevronRight, Plus, Trash2, Paperclip,
  CheckCircle2, Undo2, Ban, Loader2, AlertTriangle, ClipboardList, Receipt,
} from 'lucide-react';
import ImagemSharePoint from './ImagemSharePoint';
import EscolherAnexo from '../../EscolherAnexo';
import SelecaoPecaCentral from '../certificacao/SelecaoPecaCentral';

// =====================================================================
// Fase 2 — ANDAMENTO das Ordens de Serviço da Conservação Predial.
//
// ⚠️ DEM-00025 Fase B: as OS deixaram de NASCER da inspeção (1:1 por achado).
// Agora a não conformidade é resolvida pela BAIXA no próprio item (no detalhe do
// programa) e a OS é AVULSA — o consultor a adiciona aqui ("Adicionar OS") para
// um serviço contratado à parte. Esta tela segue sendo o andamento: quem
// executou, o que foi feito, a foto do DEPOIS e o gasto. (Programas antigos ainda
// têm as OS auto-geradas — o histórico continua legível.)
//
// ⚠️⚠️ REFEITA EM TABELA (2026-08-18, 2ª rodada). A 1ª tentativa do mesmo dia
// tinha duas abas que davam baixa do MESMO jeito (uma por cartão, outra por
// lista) e um bloco de materiais pendurado embaixo da lista de ordens — a
// Julia leu como "parece que estamos fazendo os mesmos processos" e "ter a
// ordem e abaixo um material separado deixa muito confuso". O desenho atual
// desfaz isso:
//
//  1. UMA TABELA de ordens, agrupada por ESPECIALIDADE (é assim que o serviço
//     é contratado — o pintor leva as 8 ordens de pintura numa visita).
//  2. MATERIAIS E NOTAS viraram ABA PRÓPRIA, não um bloco no rodapé: são
//     assunto diferente, com tabela própria.
//  3. UM PAINEL SÓ por ordem. O quadradinho e a seta abrem o mesmo lugar —
//     não existem dois caminhos para concluir a mesma coisa.
//
// ⚠️ OS STATUS FORAM ESCONDIDOS, NÃO REMOVIDOS. A tela fala **Em aberto /
// Concluída / Cancelada**; o `Em andamento` do banco continua existindo e é
// atravessado sozinho (ver `darBaixa`). Sumiram os botões Iniciar e Desfazer
// início, que confundiam sem decidir nada — a Manutenção quer marcar o que
// ficou pronto, não administrar uma máquina de estados.
//
// ⚠️ Concluir continua exigindo serviço descrito + foto do DEPOIS. É o que
// sustenta o Antes × Depois do relatório e o certificado.
//
// ⚠️ O mesmo material pode atender VÁRIAS ordens (`dtbConservCustoOs`). Somar
// o custo por ordem infla o gasto — o total sai da lista única da aba
// Materiais (regra pura em `conserv-os.js › casarCustosComOrdens`).
// =====================================================================

const API = '/api/manutencao/conservacao/os';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const jsonAuth = () => ({ ...auth(), 'Content-Type': 'application/json' });

// Como a tela FALA dos status do banco. 'Pendente' e 'Em andamento' são a
// mesma coisa para quem opera: ainda não ficou pronto.
const SITUACAO = {
  'Pendente': { rotulo: 'Em aberto', bg: '#fef3c7', color: '#92400e' },
  'Em andamento': { rotulo: 'Em aberto', bg: '#fef3c7', color: '#92400e' },
  'Concluído': { rotulo: 'Concluída', bg: '#d1fae5', color: '#065f46' },
  'Cancelado': { rotulo: 'Cancelada', bg: '#f1f5f9', color: '#64748b' },
};
const emAberto = (o) => o.STATUS === 'Pendente' || o.STATUS === 'Em andamento';

const FILTROS = [
  { chave: 'todas', rotulo: 'Todas', casa: () => true },
  { chave: 'abertas', rotulo: 'Em aberto', casa: emAberto },
  { chave: 'concluidas', rotulo: 'Concluídas', casa: (o) => o.STATUS === 'Concluído' },
  { chave: 'canceladas', rotulo: 'Canceladas', casa: (o) => o.STATUS === 'Cancelado' },
];

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');
const totalDe = (c) => (Number(c.QUANTIDADE) || 0) * (Number(c.VALOR_UNITARIO) || 0);

const thBase = { padding: '10px 14px', borderBottom: '1px solid #e2e8f0', fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdBase = { padding: '10px 14px', fontSize: 13.5, color: '#0f172a', verticalAlign: 'middle' };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
  borderRadius: 7, fontSize: 13, color: '#0f172a', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 };
const btn = (bg, color, border = 'transparent') => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: bg, color, border: `1px solid ${border}`, borderRadius: 8,
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
});

const ConservacaoOrdens = ({ idPrograma, onVoltar, podeEditar }) => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [expandida, setExpandida] = useState(null);   // id da OS aberta (uma por vez)
  const [gruposFechados, setGruposFechados] = useState({});
  const [filtro, setFiltro] = useState('todas');
  const [aba, setAba] = useState('ordens');           // ordens | materiais
  const [novaOs, setNovaOs] = useState(false);        // form de OS avulsa aberto?

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}/programa/${idPrograma}`, { headers: auth() });
      if (!r.ok) throw new Error((await r.json())?.message || `HTTP ${r.status}`);
      setDados(await r.json());
    } catch (e) {
      setErro(e.message || 'Erro ao carregar as ordens.');
    } finally {
      setCarregando(false);
    }
  }, [idPrograma]);

  useEffect(() => { carregar(); }, [carregar]);

  const ordens = useMemo(() => dados?.ordens || [], [dados]);
  const visiveis = useMemo(
    () => ordens.filter(FILTROS.find((f) => f.chave === filtro).casa),
    [ordens, filtro]
  );

  // Grupos por especialidade, com o que ainda dá trabalho contado — é o número
  // que decide a próxima visita do prestador. Sem especialidade cai em
  // "Outros" (mesmo rótulo dos indicadores), nunca some da tela.
  const grupos = useMemo(() => {
    const mapa = new Map();
    for (const o of visiveis) {
      const chave = o.ESPECIALIDADE || 'Outros';
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave).push(o);
    }
    return [...mapa.entries()]
      .map(([especialidade, lista]) => ({
        especialidade, ordens: lista, abertas: lista.filter(emAberto).length,
      }))
      // Mais trabalho em aberto primeiro; empate pelo nome, para a ordem não
      // dançar a cada recarga.
      .sort((a, b) => b.abertas - a.abertas || a.especialidade.localeCompare(b.especialidade));
  }, [visiveis]);

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando ordens de serviço…</p>;
  if (!dados) {
    return (
      <div>
        <button onClick={onVoltar} style={btn('#fff', '#0f172a', '#e2e8f0')}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginTop: 12 }}>{erro}</div>
      </div>
    );
  }

  const { programa, custoTotal, custosDoPrograma } = dados;
  // OS avulsa só se adiciona com o programa aberto (não certificado/cancelado).
  const programaAberto = !['Certificado', 'Cancelado', 'Rascunho'].includes(programa.STATUS);
  const abertas = ordens.filter(emAberto).length;
  const concluidas = ordens.filter((o) => o.STATUS === 'Concluído').length;
  const ativas = ordens.filter((o) => o.STATUS !== 'Cancelado').length;
  const pct = ativas > 0 ? Math.round((concluidas / ativas) * 100) : 0;
  const contar = (f) => ordens.filter(f.casa).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btn('#fff', '#0f172a', '#e2e8f0')}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Ordens de Serviço · {programa.LOJA}
        </h3>
        <span style={{ fontSize: 13, color: '#64748b' }}>Inspeção de {dataBr(programa.DATA_INSPECAO)}</span>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

      {/* --- placar enxuto: o que falta, o que ficou pronto, quanto custou --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ padding: 14, background: '#fef3c7', borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', textTransform: 'uppercase' }}>Em aberto</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#92400e', fontVariantNumeric: 'tabular-nums' }}>{abertas}</div>
        </div>
        <div style={{ padding: 14, background: '#d1fae5', borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#065f46', textTransform: 'uppercase' }}>Concluídas</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#065f46', fontVariantNumeric: 'tabular-nums' }}>{concluidas}</div>
        </div>
        <div style={{ padding: 14, background: '#eef2ff', borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#4338ca', textTransform: 'uppercase' }}>Progresso</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#312e81', fontVariantNumeric: 'tabular-nums' }}>{pct}%</div>
          <div style={{ fontSize: 12, color: '#4338ca' }}>{concluidas} de {ativas}</div>
        </div>
        <div style={{ padding: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Custo total</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{brl(custoTotal)}</div>
        </div>
      </div>

      {/* --- abas: dois assuntos, não dois caminhos para a mesma coisa --- */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {[
          { chave: 'ordens', rotulo: 'Ordens de serviço', icone: ClipboardList, n: ordens.length },
          { chave: 'materiais', rotulo: 'Materiais e notas', icone: Receipt, n: (custosDoPrograma || []).length },
        ].map((v) => {
          const ativa = aba === v.chave;
          const Icone = v.icone;
          return (
            <button key={v.chave} onClick={() => setAba(v.chave)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: ativa ? 800 : 600, fontSize: 14, color: ativa ? '#4f46e5' : '#64748b',
              borderBottom: `2px solid ${ativa ? '#4f46e5' : 'transparent'}`, marginBottom: -1,
            }}>
              <Icone size={15} /> {v.rotulo}
              <span style={{ fontVariantNumeric: 'tabular-nums', color: ativa ? '#4f46e5' : '#94a3b8' }}>({v.n})</span>
            </button>
          );
        })}
      </div>

      {aba === 'materiais' ? (
        <AbaMateriais custos={custosDoPrograma || []} ordens={ordens} idPrograma={idPrograma}
          loja={programa.LOJA} podeEditar={podeEditar} onMudou={carregar} onErro={setErro} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
            {FILTROS.map((f) => (
              <button key={f.chave} onClick={() => setFiltro(f.chave)} style={{
                ...btn(filtro === f.chave ? '#4f46e5' : '#fff', filtro === f.chave ? '#fff' : '#475569', '#e2e8f0'),
              }}>
                {f.rotulo} ({contar(f)})
              </button>
            ))}
            {/* OS avulsa (DEM-00025, Fase B): a OS não nasce mais da inspeção — o
                consultor a adiciona aqui. Só com o programa aberto. */}
            {podeEditar && programaAberto && !novaOs && (
              <button onClick={() => setNovaOs(true)} style={{ ...btn('#4f46e5', '#fff'), marginLeft: 'auto' }}>
                <Plus size={15} /> Adicionar OS
              </button>
            )}
          </div>

          {novaOs && (
            <FormOsAvulsa idPrograma={idPrograma} loja={programa.LOJA}
              ncs={dados.naoConformidadesDoPrograma || []}
              onCancelar={() => setNovaOs(false)}
              onSalvo={() => { setNovaOs(false); carregar(); }} onErro={setErro} />
          )}

          {ordens.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              Nenhuma ordem de serviço avulsa neste programa. As não conformidades da inspeção
              são resolvidas pela <strong>baixa no próprio item</strong> (no detalhe do programa);
              use <strong>Adicionar OS</strong> para um serviço contratado à parte.
            </div>
          ) : visiveis.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              {filtro === 'abertas' ? 'Nada em aberto — tudo o que a inspeção apontou já foi resolvido.'
                : `Nenhuma ordem em “${FILTROS.find((f) => f.chave === filtro).rotulo}”.`}
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr>
                      <th style={{ ...thBase, width: 38 }} />
                      <th style={{ ...thBase, textAlign: 'left' }}>Nº</th>
                      <th style={{ ...thBase, textAlign: 'left' }}>Área — problema</th>
                      <th style={{ ...thBase, textAlign: 'left' }}>Situação</th>
                      <th style={{ ...thBase, textAlign: 'left' }}>Responsável</th>
                      <th style={{ ...thBase, textAlign: 'left' }}>Execução</th>
                      <th style={{ ...thBase, textAlign: 'right' }}>Material</th>
                      <th style={{ ...thBase, width: 38 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => {
                      const fechado = !!gruposFechados[g.especialidade];
                      return (
                        <React.Fragment key={g.especialidade}>
                          {/* Cabeçalho do grupo: a especialidade e o quanto dela ainda dá trabalho. */}
                          <tr onClick={() => setGruposFechados((s) => ({ ...s, [g.especialidade]: !s[g.especialidade] }))}
                            style={{ background: '#f8fafc', cursor: 'pointer', borderTop: '1px solid #e2e8f0' }}>
                            <td colSpan={8} style={{ ...tdBase, padding: '9px 14px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {fechado ? <ChevronRight size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#4f46e5" />}
                                <strong style={{ fontSize: 14, color: '#0f172a' }}>{g.especialidade}</strong>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                  {g.ordens.length} {g.ordens.length > 1 ? 'ordens' : 'ordem'}
                                </span>
                                <span style={{
                                  padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                                  background: g.abertas > 0 ? '#fef3c7' : '#d1fae5',
                                  color: g.abertas > 0 ? '#92400e' : '#065f46',
                                }}>
                                  {g.abertas > 0 ? `${g.abertas} em aberto` : 'tudo resolvido'}
                                </span>
                              </span>
                            </td>
                          </tr>

                          {!fechado && g.ordens.map((os) => (
                            <LinhaOrdem key={os.ID} os={os} podeEditar={podeEditar}
                              ncsPrograma={dados.naoConformidadesDoPrograma || []}
                              aberta={expandida === os.ID}
                              onAlternar={() => setExpandida(expandida === os.ID ? null : os.ID)}
                              onMudou={carregar} onErro={setErro} />
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Uma ordem: a linha da tabela + o painel que ela abre.
//
// ⚠️ O quadradinho e a seta abrem O MESMO painel. Ter dois caminhos para
// concluir a mesma ordem foi o que tornou a tela anterior confusa.
// ---------------------------------------------------------------------
const LinhaOrdem = ({ os, podeEditar, ncsPrograma = [], aberta, onAlternar, onMudou, onErro }) => {
  const [servico, setServico] = useState(os.SERVICO_EXECUTADO || '');
  const [responsavel, setResponsavel] = useState(os.RESPONSAVEL || '');
  const [dataExec, setDataExec] = useState((os.DATA_EXECUCAO || '').slice(0, 10));
  const [observacoes, setObservacoes] = useState(os.OBSERVACOES || '');
  const [salvando, setSalvando] = useState(false);
  const [editandoNcs, setEditandoNcs] = useState(false);
  const [ncsSel, setNcsSel] = useState((os.naoConformidades || []).map((n) => n.ID));

  const sit = SITUACAO[os.STATUS] || SITUACAO['Pendente'];
  const concluida = os.STATUS === 'Concluído';
  const cancelada = os.STATUS === 'Cancelado';

  // Espelha `pendenciasParaConcluir` do servidor só para EXPLICAR o bloqueio.
  // ⚠️ DEM-00025 Fase B (29/09): a foto do depois saiu da OS — concluir exige só
  // o serviço descrito (a prova do conserto é a baixa no item, por NC).
  const falta = [
    !servico.trim() && 'descrever o serviço',
  ].filter(Boolean);

  const mudarStatus = async (novo) => {
    const r = await fetch(`${API}/${os.ID}/status`, {
      method: 'PUT', headers: jsonAuth(), body: JSON.stringify({ status: novo }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error([d?.message, ...(d?.erros || [])].filter(Boolean).join(' '));
  };

  const salvarExecucao = async () => {
    const r = await fetch(`${API}/${os.ID}`, {
      method: 'PUT', headers: jsonAuth(),
      body: JSON.stringify({
        responsavel, data_execucao: dataExec || null,
        servico_executado: servico, observacoes,
      }),
    });
    if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao salvar.');
  };

  const acao = async (fn) => {
    setSalvando(true);
    try { await fn(); onMudou(); }
    catch (e) { onErro(e.message); }
    finally { setSalvando(false); }
  };

  // Dar baixa: grava a execução e leva a ordem a Concluído.
  // ⚠️ 'Pendente' NÃO vai direto a 'Concluído' — a máquina de estados do
  // servidor (conserv-os.js › TRANSICOES) exige passar por 'Em andamento'. A
  // tela atravessa isso sozinha: quem opera não deveria administrar estado.
  const darBaixa = () => acao(async () => {
    await salvarExecucao();
    if (os.STATUS === 'Pendente') await mudarStatus('Em andamento');
    await mudarStatus('Concluído');
  });

  // Cancelar: idem — de 'Em andamento' o servidor só cancela voltando antes.
  const cancelar = () => acao(async () => {
    if (!window.confirm(`Cancelar a ordem ${os.NUMERO}? Ela sai da lista de trabalho, mas continua no histórico.`)) return;
    if (os.STATUS === 'Em andamento') await mudarStatus('Pendente');
    await mudarStatus('Cancelado');
  });

  const reabrir = () => acao(() => mudarStatus('Em andamento'));
  const reativar = () => acao(() => mudarStatus('Pendente'));

  // Salva quais não conformidades esta OS cobre (N:N). Concluir a OS dá baixa nelas.
  const salvarNcs = () => acao(async () => {
    const r = await fetch(`${API}/${os.ID}/itens`, {
      method: 'PUT', headers: jsonAuth(), body: JSON.stringify({ itens: ncsSel }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d?.message || 'Erro ao vincular não conformidades.');
    setEditandoNcs(false);
  });

  return (
    <>
      <tr onClick={onAlternar} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: aberta ? '#faf5ff' : '#fff' }}>
        <td style={{ ...tdBase, textAlign: 'center' }}>
          {/* A marca é o ESTADO da ordem, não um comando: marcar abre o painel
              onde a baixa acontece (com foto e serviço). Sem isso, o clique
              concluiria sem evidência — que é o que o certificado atesta. */}
          <input type="checkbox" readOnly checked={concluida} disabled={cancelada}
            onClick={(e) => { e.stopPropagation(); onAlternar(); }}
            title={concluida ? 'Concluída' : 'Marcar como concluída'}
            style={{ width: 16, height: 16, accentColor: '#059669', cursor: cancelada ? 'default' : 'pointer' }} />
        </td>
        <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: 11.5, fontWeight: 800, color: '#4338ca', whiteSpace: 'nowrap' }}>
          {os.NUMERO}
        </td>
        <td style={{ ...tdBase, minWidth: 260 }}>
          <span style={{ color: cancelada ? '#94a3b8' : '#0f172a', textDecoration: cancelada ? 'line-through' : 'none' }}>
            <strong style={{ fontWeight: 700 }}>{os.AREA}</strong> — {os.DESCRICAO_PROBLEMA}
          </span>
        </td>
        <td style={tdBase}>
          <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: sit.bg, color: sit.color, whiteSpace: 'nowrap' }}>
            {sit.rotulo}
          </span>
        </td>
        <td style={{ ...tdBase, color: '#475569', fontSize: 13 }}>{os.RESPONSAVEL || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
        <td style={{ ...tdBase, color: '#475569', fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {os.DATA_EXECUCAO ? dataBr(os.DATA_EXECUCAO) : <span style={{ color: '#cbd5e1' }}>—</span>}
        </td>
        {/* ⚠️ O QUE foi usado, nunca QUANTO: um valor por linha convida a somar
            a coluna, e o material compartilhado aparece inteiro em cada ordem
            que atendeu — a soma daria bem mais que o gasto real do programa.
            O dinheiro tem um lugar só: a aba Materiais e notas. */}
        <td style={{ ...tdBase, fontSize: 12.5, color: '#475569', maxWidth: 200 }}>
          {(os.custos || []).length === 0
            ? <span style={{ color: '#cbd5e1' }}>—</span>
            : (
              <span title={(os.custos || []).map((c) => `${c.DESCRICAO} — ${brl(totalDe(c))}`).join(' · ')}>
                {os.custos.length === 1
                  ? <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{os.custos[0].DESCRICAO}</span>
                  : `${os.custos.length} itens`}
              </span>
            )}
        </td>
        <td style={{ ...tdBase, textAlign: 'center' }}>
          {aberta ? <ChevronDown size={17} color="#4f46e5" /> : <ChevronRight size={17} color="#cbd5e1" />}
        </td>
      </tr>

      {aberta && (
        <tr>
          <td colSpan={8} style={{ padding: 0, background: '#faf5ff', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ padding: 16 }}>
              {/* ⚠️ DEM-00025 Fase B (29/09): a OS não tem mais foto Antes/Depois
                  própria — a prova do conserto é a BAIXA NO ITEM, mostrada por não
                  conformidade logo abaixo. A OS é organização (NCs + custo + status). */}

              {/* --- não conformidades cobertas (DEM-00025 Fase B) --- */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={labelStyle}>Não conformidades cobertas por esta OS</div>
                  {podeEditar && !cancelada && (
                    <button onClick={() => { setNcsSel((os.naoConformidades || []).map((n) => n.ID)); setEditandoNcs((v) => !v); }}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: '#4338ca' }}>
                      {editandoNcs ? 'fechar' : 'alterar'}
                    </button>
                  )}
                </div>
                {editandoNcs ? (
                  <div>
                    <SeletorNCs ncs={ncsPrograma} selecionadas={ncsSel}
                      onAlternar={(id) => setNcsSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={salvarNcs} disabled={salvando} style={btn('#4f46e5', '#fff')}>
                        {salvando ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} Salvar
                      </button>
                      <button onClick={() => setEditandoNcs(false)} style={btn('#f1f5f9', '#475569', '#e2e8f0')}>Cancelar</button>
                    </div>
                  </div>
                ) : (os.naoConformidades || []).length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12.5, color: '#94a3b8' }}>
                    Nenhuma — esta OS é um serviço avulso, sem vínculo com a inspeção.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {os.naoConformidades.map((nc) => (
                      <div key={nc.ID} style={{ padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{nc.AREA_SNAPSHOT} — {nc.ITEM_SNAPSHOT}</span>
                          {nc.BAIXA_EM
                            ? <span style={{ fontSize: 10.5, fontWeight: 800, color: '#065f46', background: '#d1fae5', padding: '1px 7px', borderRadius: 999 }}>baixa no item</span>
                            : concluida
                              ? <span style={{ fontSize: 10.5, fontWeight: 800, color: '#065f46', background: '#d1fae5', padding: '1px 7px', borderRadius: 999 }}>resolvida pela OS</span>
                              : <span style={{ fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fef3c7', padding: '1px 7px', borderRadius: 999 }}>pendente</span>}
                        </div>
                        {nc.OBSERVACAO && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{nc.OBSERVACAO}</div>}
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                          {(nc.FOTOS || []).length > 0 && (
                            <div>
                              <div style={{ ...labelStyle, color: '#b45309' }}>ANTES</div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {nc.FOTOS.map((u) => <ImagemSharePoint key={u} url={u} alt="Antes" borderColor="#fde68a" />)}
                              </div>
                            </div>
                          )}
                          {(nc.FOTOS_DEPOIS || []).length > 0 && (
                            <div>
                              <div style={{ ...labelStyle, color: '#059669' }}>DEPOIS (baixa no item)</div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {nc.FOTOS_DEPOIS.map((u) => <ImagemSharePoint key={u} url={u} alt="Depois" borderColor="#a7f3d0" />)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* --- execução --- */}
              {podeEditar && !cancelada ? (
                <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                  <div>
                    <label style={labelStyle}>O que foi feito {!concluida && '*'}</label>
                    <textarea value={servico} rows={2} onChange={(e) => setServico(e.target.value)}
                      placeholder="Serviço executado para resolver o problema"
                      style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Responsável</label>
                      <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Data da execução</label>
                      <input type="date" value={dataExec} onChange={(e) => setDataExec(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Observações</label>
                      <input value={observacoes} onChange={(e) => setObservacoes(e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 14, fontSize: 13, color: '#475569' }}>
                  <div><strong>Responsável:</strong> {os.RESPONSAVEL || '—'} · <strong>Data:</strong> {dataBr(os.DATA_EXECUCAO)}</div>
                  {os.SERVICO_EXECUTADO && <div style={{ marginTop: 4 }}><strong>Serviço:</strong> {os.SERVICO_EXECUTADO}</div>}
                </div>
              )}

              {/* --- materiais que passaram por esta ordem (leitura; lançar é na aba) --- */}
              {(os.custos || []).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={labelStyle}>Materiais e serviços usados aqui</div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {os.custos.map((c) => (
                      <div key={c.ID} style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5,
                      }}>
                        <span style={{ flex: 1, minWidth: 140, color: '#0f172a' }}>{c.DESCRICAO}</span>
                        {c.FORNECEDOR && <span style={{ color: '#64748b' }}>{c.FORNECEDOR}</span>}
                        {(c.ORDENS || []).length > 1 && (
                          <span title="Este lançamento atende mais de uma ordem" style={{ fontSize: 11, color: '#7c3aed', background: '#f3e8ff', padding: '1px 7px', borderRadius: 999, fontWeight: 700 }}>
                            dividido com outras {(c.ORDENS || []).length - 1}
                          </span>
                        )}
                        <span style={{ fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{brl(totalDe(c))}</span>
                      </div>
                    ))}
                  </div>
                  {/* Sem este aviso, quem soma as linhas da tabela acha que o
                      programa gastou mais do que gastou. */}
                  {os.custos.some((c) => (c.ORDENS || []).length > 1) && (
                    <p style={{ margin: '6px 0 0', fontSize: 11.5, color: '#94a3b8' }}>
                      Item dividido com outra ordem aparece inteiro aqui — o gasto do programa está na aba <strong>Materiais e notas</strong>.
                    </p>
                  )}
                </div>
              )}

              {/* --- ações: uma decisão por vez --- */}
              {podeEditar && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #e9d5ff' }}>
                  {emAberto(os) && (
                    <>
                      <button onClick={darBaixa} disabled={salvando || falta.length > 0}
                        style={{ ...btn('#059669', '#fff'), opacity: falta.length ? 0.5 : 1 }}>
                        {salvando ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} Dar baixa
                      </button>
                      <button onClick={() => acao(salvarExecucao)} disabled={salvando} style={btn('#fff', '#4338ca', '#c7d2fe')}>
                        Salvar sem concluir
                      </button>
                      <button onClick={cancelar} disabled={salvando} style={btn('#fff', '#b91c1c', '#fecaca')}>
                        <Ban size={14} /> Cancelar ordem
                      </button>
                      {/* Botão travado sem explicação é o jeito mais rápido de travar o usuário. */}
                      {falta.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                          <AlertTriangle size={14} /> Para dar baixa, falta {falta.join(' e ')}.
                        </span>
                      )}
                    </>
                  )}
                  {concluida && (
                    <button onClick={reabrir} disabled={salvando} style={btn('#f1f5f9', '#475569', '#e2e8f0')}>
                      <Undo2 size={14} /> Reabrir
                    </button>
                  )}
                  {cancelada && (
                    <button onClick={reativar} disabled={salvando} style={btn('#f1f5f9', '#475569', '#e2e8f0')}>
                      <Undo2 size={14} /> Reativar
                    </button>
                  )}
                  {os.ATUALIZADO_POR && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                      Última alteração por {os.ATUALIZADO_POR}
                    </span>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ---------------------------------------------------------------------
// ABA MATERIAIS E NOTAS — o dinheiro do programa, em tabela
//
// ⚠️ É AQUI que mora o total: cada lançamento aparece UMA vez, com as ordens
// que ele atendeu. Na aba de ordens, o mesmo material aparece inteiro em cada
// ordem — somar aquilo conta o gasto várias vezes.
// ---------------------------------------------------------------------
const AbaMateriais = ({ custos, ordens, idPrograma, loja, podeEditar, onMudou, onErro }) => {
  const [novo, setNovo] = useState(false);
  const [editandoVinculo, setEditandoVinculo] = useState(null);
  const total = custos.reduce((s, c) => s + totalDe(c), 0);
  const numeroDaOs = (id) => (ordens.find((o) => o.ID === id) || {}).NUMERO || `#${id}`;
  // O chip mostra o código (curto); o texto da ordem vai no hover.
  const descricaoDaOs = (id) => {
    const o = ordens.find((x) => x.ID === id);
    return o ? `${o.AREA} — ${o.DESCRICAO_PROBLEMA}` : '';
  };

  const remover = async (id) => {
    if (!window.confirm('Remover este lançamento?')) return;
    try {
      const r = await fetch(`${API}/custo/${id}`, { method: 'DELETE', headers: auth() });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao remover.');
      onMudou();
    } catch (e) { onErro(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 12.5, color: '#94a3b8', flex: 1, minWidth: 260 }}>
          Cada material ou serviço é lançado uma vez, com as ordens que ele atendeu — o mesmo item pode servir a várias.
          Sem ordem marcada, é custo do programa inteiro. A nota fiscal é opcional.
        </p>
        {podeEditar && !novo && (
          <button onClick={() => setNovo(true)} style={btn('#4f46e5', '#fff')}>
            <Plus size={15} /> Lançar material/serviço
          </button>
        )}
      </div>

      {novo && (
        <FormCusto idPrograma={idPrograma} loja={loja} ordens={ordens} ordensIniciais={[]}
          onCancelar={() => setNovo(false)}
          onSalvo={() => { setNovo(false); onMudou(); }} onErro={onErro} />
      )}

      {custos.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum material ou serviço lançado neste programa.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ ...thBase, textAlign: 'left' }}>Tipo</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Descrição</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Fornecedor / Empresa</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>NF</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Qtd × unit.</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Total</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Ordens atendidas</th>
                  {podeEditar && <th style={{ ...thBase, textAlign: 'right' }}>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {custos.map((c) => (
                  <React.Fragment key={c.ID}>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdBase}>
                        <span style={{
                          padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                          background: c.TIPO === 'Servico' ? '#f3e8ff' : '#e0e7ff',
                          color: c.TIPO === 'Servico' ? '#6b21a8' : '#3730a3',
                        }}>
                          {c.TIPO === 'Servico' ? 'SERVIÇO' : 'MATERIAL'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, minWidth: 200 }}>{c.DESCRICAO}</td>
                      <td style={{ ...tdBase, color: '#475569' }}>{c.FORNECEDOR || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ ...tdBase, fontVariantNumeric: 'tabular-nums' }}>
                        {/* A NF é opcional: "—" aqui não é pendência a resolver. */}
                        {c.NF_NUMERO ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            {c.NF_NUMERO}
                            {c.NF_URL && (
                              <a href={c.NF_URL} target="_blank" rel="noopener noreferrer" title="Abrir anexo da nota"
                                style={{ display: 'inline-flex', color: '#4338ca' }}>
                                <Paperclip size={13} />
                              </a>
                            )}
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', color: '#475569', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {Number(c.QUANTIDADE)} × {brl(c.VALOR_UNITARIO)}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        {brl(totalDe(c))}
                      </td>
                      <td style={tdBase}>
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          {(c.ORDENS || []).length === 0
                            ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>programa inteiro</span>
                            : c.ORDENS.map((idOs) => (
                              <span key={idOs} title={descricaoDaOs(idOs)} style={{
                                fontFamily: 'monospace', fontSize: 10.5, fontWeight: 800, color: '#4338ca',
                                background: '#eef2ff', padding: '1px 6px', borderRadius: 999,
                              }}>
                                {numeroDaOs(idOs)}
                              </span>
                            ))}
                        </span>
                      </td>
                      {podeEditar && (
                        <td style={{ ...tdBase, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            {/* Editar o vínculo é ação da linha, não parte da lista
                                de ordens — junto dos chips ele quebrava a leitura. */}
                            <button onClick={() => setEditandoVinculo(editandoVinculo === c.ID ? null : c.ID)}
                              title="Escolher quais ordens este lançamento atendeu"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: '#4338ca' }}>
                              {editandoVinculo === c.ID ? 'fechar' : 'alterar ordens'}
                            </button>
                            <button onClick={() => remover(c.ID)} title="Remover lançamento"
                              style={{ display: 'inline-flex', padding: 4, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 6, cursor: 'pointer' }}>
                              <Trash2 size={13} />
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                    {editandoVinculo === c.ID && podeEditar && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <EditorVinculo custo={c} ordens={ordens}
                            onFechar={() => setEditandoVinculo(null)}
                            onSalvo={() => { setEditandoVinculo(null); onMudou(); }} onErro={onErro} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr style={{ background: '#f8fafc' }}>
                  <td colSpan={5} style={{ ...tdBase, textAlign: 'right', fontWeight: 800, color: '#475569' }}>Total do programa</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{brl(total)}</td>
                  <td colSpan={podeEditar ? 2 : 1} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Marca/desmarca quais ordens o lançamento atendeu.
const EditorVinculo = ({ custo, ordens, onFechar, onSalvo, onErro }) => {
  const [sel, setSel] = useState(custo.ORDENS || []);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await fetch(`${API}/custo/${custo.ID}/ordens`, {
        method: 'PUT', headers: jsonAuth(), body: JSON.stringify({ ordens: sel }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao vincular.');
      onSalvo();
    } catch (e) { onErro(e.message); } finally { setSalvando(false); }
  };

  return (
    <div style={{ padding: 14 }}>
      <SeletorOrdens ordens={ordens} selecionadas={sel}
        onAlternar={(id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={salvar} disabled={salvando} style={btn('#4f46e5', '#fff')}>
          {salvando ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} Salvar
        </button>
        <button onClick={onFechar} style={btn('#fff', '#475569', '#e2e8f0')}>Cancelar</button>
      </div>
    </div>
  );
};

// Ordens com caixa de seleção, agrupadas por especialidade — quem compra tinta
// marca as ordens de pintura, e elas estão juntas.
//
// ⚠️ AQUI NÃO APARECE O NÚMERO DA OS (pedido da Julia, 2026-08-18): na hora de
// lançar um material, quem escolhe reconhece a ordem pelo PROBLEMA ("Área
// Externa — piso do estacionamento"), não por "CP-2026-VEL-002". O código
// ocupava a largura que o texto precisava e ainda obrigava a cortá-lo. Ele
// continua nos chips da tabela de materiais, onde o papel é justamente
// identificar em pouco espaço.
const SeletorOrdens = ({ ordens, selecionadas, onAlternar }) => {
  const porEspecialidade = useMemo(() => {
    const mapa = new Map();
    for (const o of ordens) {
      const k = o.ESPECIALIDADE || 'Outros';
      if (!mapa.has(k)) mapa.set(k, []);
      mapa.get(k).push(o);
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ordens]);

  if (ordens.length === 0) {
    return <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Este programa não tem ordens de serviço.</p>;
  }
  // A altura acomoda ~12 ordens sem rolagem (o tamanho de um programa real);
  // acima disso rola, mas o padding embaixo evita cortar uma linha no meio.
  return (
    <div style={{
      display: 'grid', gap: 12, maxHeight: 440, overflowY: 'auto', paddingBottom: 4,
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    }}>
      {porEspecialidade.map(([esp, lista]) => (
        <div key={esp}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>{esp}</div>
          <div style={{ display: 'grid', gap: 3 }}>
            {lista.map((o) => (
              <label key={o.ID} title={o.NUMERO} style={{
                display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5,
                color: '#334155', cursor: 'pointer', padding: '4px 4px',
                background: selecionadas.includes(o.ID) ? '#eef2ff' : 'transparent',
                borderRadius: 6,
              }}>
                <input type="checkbox" checked={selecionadas.includes(o.ID)} onChange={() => onAlternar(o.ID)}
                  style={{ width: 15, height: 15, marginTop: 1, accentColor: '#4f46e5', cursor: 'pointer', flexShrink: 0 }} />
                {/* Texto inteiro, quebrando em quantas linhas precisar: cortar o
                    problema no meio é o que impedia de reconhecer a ordem. */}
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                  <strong style={{ fontWeight: 700 }}>{o.AREA}</strong> — {o.DESCRICAO_PROBLEMA}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------
// Formulário de OS AVULSA (DEM-00025, Fase B). Área + descrição + especialidade.
// A OS nasce 'Pendente'; o "antes/depois" e o custo entram pelo fluxo normal da
// ordem. ⚠️ A lista de especialidades espelha a do servidor (ESPECIALIDADES em
// server-conservacao.js) — é por ela que a tela agrupa as OS.
// ---------------------------------------------------------------------
const ESPECIALIDADES_OS = [
  'Pintura', 'Marcenaria', 'Civil', 'Serralheria', 'Elétrica',
  'Hidráulica', 'Comunicação Visual', 'Paisagismo', 'Outros',
];

// Seletor de NÃO CONFORMIDADES da inspeção (agrupadas por área), com caixa de
// seleção — o espelho do SeletorOrdens dos custos. É por aqui que a OS escolhe
// quais NCs cobre. Marca em verde a que já teve baixa.
const SeletorNCs = ({ ncs, selecionadas, onAlternar }) => {
  const porArea = useMemo(() => {
    const mapa = new Map();
    for (const nc of ncs) {
      const k = nc.AREA_SNAPSHOT || '(sem área)';
      if (!mapa.has(k)) mapa.set(k, []);
      mapa.get(k).push(nc);
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ncs]);

  if (ncs.length === 0) {
    return <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Este programa não tem não conformidades registradas.</p>;
  }
  return (
    <div style={{
      display: 'grid', gap: 12, maxHeight: 340, overflowY: 'auto', paddingBottom: 4,
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    }}>
      {porArea.map(([area, lista]) => (
        <div key={area}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>{area}</div>
          <div style={{ display: 'grid', gap: 3 }}>
            {lista.map((nc) => (
              <label key={nc.ID} style={{
                display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, color: '#334155',
                cursor: 'pointer', padding: '4px', borderRadius: 6,
                background: selecionadas.includes(nc.ID) ? '#eef2ff' : 'transparent',
              }}>
                <input type="checkbox" checked={selecionadas.includes(nc.ID)} onChange={() => onAlternar(nc.ID)}
                  style={{ width: 15, height: 15, marginTop: 1, accentColor: '#4f46e5', cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                  <strong style={{ fontWeight: 700 }}>{nc.ITEM_SNAPSHOT}</strong>
                  {nc.OBSERVACAO ? ` — ${nc.OBSERVACAO}` : ''}
                  {nc.BAIXA_EM && <span style={{ color: '#059669', fontWeight: 700 }}> · com baixa</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const FormOsAvulsa = ({ idPrograma, loja, ncs, onCancelar, onSalvo, onErro }) => {
  const [f, setF] = useState({ descricao_problema: '', especialidade: '' });
  const [itensSel, setItensSel] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!f.descricao_problema.trim()) { onErro('Descreva o serviço da OS.'); return; }
    setSalvando(true);
    try {
      const r = await fetch(API, {
        method: 'POST', headers: jsonAuth(),
        body: JSON.stringify({ ...f, id_programa: idPrograma, itens: itensSel }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao criar a OS.');
      onSalvo();
    } catch (e) { onErro(e.message); } finally { setSalvando(false); }
  };

  return (
    <div style={{ marginBottom: 14, padding: 14, background: '#fff', border: '1px solid #c7d2fe', borderRadius: 12, display: 'grid', gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#4338ca' }}>Nova ordem de serviço avulsa · {loja}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Descrição do serviço *</label>
          <textarea value={f.descricao_problema} rows={2}
            onChange={(e) => setF({ ...f, descricao_problema: e.target.value })}
            placeholder="O que será feito nesta ordem"
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div>
          <label style={labelStyle}>Especialidade</label>
          <select value={f.especialidade} onChange={(e) => setF({ ...f, especialidade: e.target.value })} style={inputStyle}>
            <option value="">— escolher —</option>
            {ESPECIALIDADES_OS.map((e2) => <option key={e2} value={e2}>{e2}</option>)}
          </select>
        </div>
      </div>
      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
        <label style={labelStyle}>Não conformidades cobertas por esta OS ({itensSel.length} marcada{itensSel.length === 1 ? '' : 's'})</label>
        <p style={{ margin: '0 0 8px', fontSize: 11.5, color: '#94a3b8' }}>
          Marque as não conformidades da inspeção que este serviço resolve — concluir a OS dá baixa nelas.
          Sem nenhuma marcada, é um serviço avulso (sem vínculo).
        </p>
        <SeletorNCs ncs={ncs} selecionadas={itensSel}
          onAlternar={(id) => setItensSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={salvar} disabled={salvando} style={btn('#4f46e5', '#fff')}>
          {salvando ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Criar OS
        </button>
        <button onClick={onCancelar} style={btn('#f1f5f9', '#475569', '#e2e8f0')}>Cancelar</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Formulário de lançamento de custo
// ---------------------------------------------------------------------
const FormCusto = ({ idPrograma, loja, ordens, ordensIniciais = [], onCancelar, onSalvo, onErro }) => {
  const [f, setF] = useState({
    tipo: 'Material', origem: 'Fora', id_catalogo: null, codigo: '',
    descricao: '', fornecedor: '', quantidade: 1, valor_unitario: '', nf_numero: '', nf_url: '',
  });
  const [ordensSel, setOrdensSel] = useState(ordensIniciais);
  const [salvando, setSalvando] = useState(false);
  const [enviandoNf, setEnviandoNf] = useState(false);

  const enviarNf = async (file) => {
    if (!file) return;
    setEnviandoNf(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      fd.append('loja', loja || ''); fd.append('numero', 'PROG'); fd.append('tipo', 'nf');
      const r = await fetch(`${API}/upload-anexo`, { method: 'POST', headers: auth(), body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Falha no upload.');
      setF((x) => ({ ...x, nf_url: d.url }));
    } catch (e) { onErro(e.message); } finally { setEnviandoNf(false); }
  };

  const salvar = async () => {
    if (!f.descricao.trim()) { onErro('Informe a descrição do material/serviço.'); return; }
    setSalvando(true);
    try {
      const r = await fetch(`${API}/custo`, {
        method: 'POST', headers: jsonAuth(),
        body: JSON.stringify({ ...f, id_programa: idPrograma, ordens: ordensSel }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao lançar.');
      onSalvo();
    } catch (e) { onErro(e.message); } finally { setSalvando(false); }
  };

  return (
    <div style={{ marginBottom: 14, padding: 14, background: '#fff', border: '1px solid #c7d2fe', borderRadius: 12, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['Material', 'Servico'].map((t) => (
          <button key={t} onClick={() => setF({ ...f, tipo: t })}
            style={btn(f.tipo === t ? '#4f46e5' : '#fff', f.tipo === t ? '#fff' : '#475569', '#e2e8f0')}>
            {t === 'Servico' ? 'Serviço terceirizado' : 'Material'}
          </button>
        ))}
        {/* O catálogo da Central só faz sentido para MATERIAL — serviço
            terceirizado não é peça de estoque. */}
        {f.tipo === 'Material' && ['Fora', 'Central'].map((o) => (
          <button key={o} onClick={() => setF({ ...f, origem: o, id_catalogo: null, codigo: '', descricao: '', fornecedor: '' })}
            style={btn(f.origem === o ? '#eef2ff' : '#fff', f.origem === o ? '#4338ca' : '#94a3b8', '#e2e8f0')}>
            {o === 'Central' ? 'Da Central de Pedidos' : 'Digitar à mão'}
          </button>
        ))}
      </div>

      {f.tipo === 'Material' && f.origem === 'Central' && !f.id_catalogo && (
        <SelecaoPecaCentral onEscolher={(p) => setF({
          // Snapshot do catálogo: o preço de referência entra como sugestão,
          // mas quem manda é a nota fiscal — por isso fica editável.
          ...f, id_catalogo: p.ID, codigo: p.CODIGO, descricao: p.DESCRICAO,
          fornecedor: p.FORNECEDOR, valor_unitario: p.PRECO_REF ?? '',
        })} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>{f.tipo === 'Servico' ? 'Descrição do serviço *' : 'Descrição do material *'}</label>
          <input value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })}
            readOnly={f.origem === 'Central' && !!f.id_catalogo}
            style={{ ...inputStyle, background: (f.origem === 'Central' && f.id_catalogo) ? '#f1f5f9' : '#fff' }} />
        </div>
        <div>
          <label style={labelStyle}>{f.tipo === 'Servico' ? 'Empresa' : 'Fornecedor'}</label>
          <input value={f.fornecedor} onChange={(e) => setF({ ...f, fornecedor: e.target.value })}
            readOnly={f.origem === 'Central' && !!f.id_catalogo}
            style={{ ...inputStyle, background: (f.origem === 'Central' && f.id_catalogo) ? '#f1f5f9' : '#fff' }} />
        </div>
        <div>
          <label style={labelStyle}>Quantidade</label>
          <input type="number" min="0" step="0.01" value={f.quantidade}
            onChange={(e) => setF({ ...f, quantidade: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Valor unitário (R$)</label>
          <input type="number" min="0" step="0.01" value={f.valor_unitario}
            onChange={(e) => setF({ ...f, valor_unitario: e.target.value })} style={inputStyle} />
        </div>
        <div>
          {/* "se houver": a nota é opcional, e o rótulo diz isso — senão o campo
              vazio parece pendência a resolver. */}
          <label style={labelStyle}>Nº da nota fiscal (se houver)</label>
          <input value={f.nf_numero} onChange={(e) => setF({ ...f, nf_numero: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Anexo da NF (opcional)</label>
          <EscolherAnexo
            accept="image/*,application/pdf"
            cor="#4f46e5"
            compacto
            ocupado={enviandoNf}
            nome={f.nf_url ? 'NF anexada' : ''}
            rotulo="Anexar (PDF/imagem)"
            onArquivo={enviarNf}
          />
        </div>
      </div>

      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
        <label style={labelStyle}>Ordens atendidas ({ordensSel.length} marcada{ordensSel.length === 1 ? '' : 's'})</label>
        <p style={{ margin: '0 0 8px', fontSize: 11.5, color: '#94a3b8' }}>
          Marque todas as ordens que este material ou serviço cobriu — ele é lançado uma vez só.
          Sem nenhuma marcada, entra como custo do programa inteiro.
        </p>
        <SeletorOrdens ordens={ordens || []} selecionadas={ordensSel}
          onAlternar={(id) => setOrdensSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={salvar} disabled={salvando} style={btn('#4f46e5', '#fff')}>
          {salvando ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Lançar
        </button>
        <button onClick={onCancelar} style={btn('#f1f5f9', '#475569', '#e2e8f0')}>Cancelar</button>
      </div>
    </div>
  );
};

export default ConservacaoOrdens;
